import React, {
  useState,
  useCallback,
  useMemo,
  useRef,
  useEffect,
} from 'react';
import styled from 'styled-components';
import { Signal, useComputed, useSignalEffect } from '@preact/signals-react';
import { useSignals } from '@preact/signals-react/runtime';
import { ChatMessages } from '@/features/ai-agents/chat/chat-messages';
import {
  ChatInput,
  ChatInputHandle,
} from '@/features/ai-agents/components/chat-input';
import { useFileDropDetector } from '@/features/ai-agents/shared/use-file-drop-detector';
import { BackdropSpinner } from '@/components/backdrop-spinner';
import { useChatSession } from '@/features/ai-agents/chat/context';
import { AiConversationStarter } from '@/features/ai-agents/chat/starters';
import { FollowupSuggestions } from '@/features/ai-agents/chat/followup-suggestions';
import { useFeatureBit } from '@/features/account-context';
import { FeatureBit } from '@/webapi/use-auth-api';
import { extractRecommendations } from '@/features/ai-agents/chat/recommendations';
import {
  ContextualAttachment,
  SelectedAgent,
  chatNow,
} from '@/features/ai-agents/shared/models';
import { AiPromptMessage } from '@/features/editor/context/use-device-preview';

export const CHAT_INPUT_FLOATING_CONTAINER = `chat-input-floating-container`;

export interface ChatAreaProps {
  enableFilesUpload?: boolean;
  enableExamples?: boolean;
  enableVoiceInput?: boolean;
  enableAgentSelection?: boolean;
  showReasoning?: boolean;
  showTools?: boolean;

  inputBottom?: string;
  inputLeft?: string;
  inputRight?: string;

  starters?: AiConversationStarter;
  contextualAttachments?: Signal<ContextualAttachment[]>;
  onEditAttachment?: (
    oldAttachment: ContextualAttachment,
    newSelector: string,
  ) => void;
  onAgentSelect?: (agent: SelectedAgent) => void;
  placeholder?: string;
  initialMessage?: AiPromptMessage;
  emptyMessage?: string;
}

export function ChatArea({
  enableFilesUpload,
  enableExamples,
  enableVoiceInput,
  enableAgentSelection,
  showReasoning,
  showTools,
  inputBottom,
  inputLeft,
  inputRight,
  starters,
  contextualAttachments,
  onEditAttachment,
  onAgentSelect,
  placeholder,
  initialMessage,
  emptyMessage,
}: ChatAreaProps) {
  useSignals();
  const dropzoneVisible = useFileDropDetector();
  const {
    agent: selectedAgent,
    isLoadingSession,
    sendMessage: sendUserMessage,
    isProcessing,
    abortMessage,
    loadSession,
    currentSessionId,
    previousSessionId,
    messages,
  } = useChatSession();

  useSignalEffect(() => {
    if (
      currentSessionId.value !== previousSessionId.value &&
      !isLoadingSession.peek()
    ) {
      loadSession().then();
    }
  });

  const [isScrollButtonsShown, setIsScrollButtonsShown] = useState(false);
  const [inputWrapperHeight, setInputWrapperHeight] = useState(0);
  const inputWrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!inputWrapperRef.current) return undefined;

    const resizeObserver = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        const { height } = entry.contentRect;
        setInputWrapperHeight(height);
      });
    });

    resizeObserver.observe(inputWrapperRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const showStarters = useComputed(
    () => messages?.value?.length <= 0 && !!starters && !isLoadingSession.value,
  );

  const chatRef = React.useRef<ChatInputHandle>(null);

  const starterSendMessage = useCallback(
    (prompt: string, sendPrompt = true) => {
      chatRef.current?.setPrompt(prompt);
      if (sendPrompt) {
        setTimeout(() => {
          chatRef.current?.send();
        }, 100);
      }
    },
    [],
  );

  useEffect(() => {
    const handleAiChatInput = (ev: Event) => {
      const message = (ev as CustomEvent<string>).detail;
      if (message) {
        starterSendMessage(message);
      }
    };

    window.addEventListener(`ai-chat-input`, handleAiChatInput);

    return () => window.removeEventListener(`ai-chat-input`, handleAiChatInput);
  }, [starterSendMessage]);

  const starterAttachFile = useCallback(() => {
    chatRef.current?.openDropzone();
  }, []);

  const starterAttachSketch = useCallback(() => {
    chatRef.current?.openWhiteboard();
  }, []);

  // Memoize starters node so it's not re-created on parent re-renders
  const startersNode = useMemo(
    () =>
      starters?.(starterSendMessage, starterAttachFile, starterAttachSketch) ??
      null,
    [starters, starterSendMessage, starterAttachFile, starterAttachSketch],
  );

  const hasSentInitialMessage = useRef(false);
  useEffect(() => {
    if (
      initialMessage &&
      !hasSentInitialMessage.current &&
      !isLoadingSession.value &&
      messages.value.length === 0 &&
      chatRef.current
    ) {
      hasSentInitialMessage.current = true;

      sendUserMessage({
        role: `user`,
        content: initialMessage.prompt,
        created_at: chatNow(),
        images: initialMessage.files,
        contextualAttachments:
          contextualAttachments?.value?.length > 0
            ? contextualAttachments?.value
            : undefined,
      });
    }
  }, [
    initialMessage,
    isLoadingSession.value,
    messages.value.length,
    sendUserMessage,
    contextualAttachments,
  ]);

  const followupsEnabled = useFeatureBit(FeatureBit.SHOW_AI_FOLLOWUPS);

  const recommendations = useComputed(() => {
    if (!isProcessing.value) {
      const msgs = messages.value;
      if (!msgs || msgs.length === 0) return [];
      const lastMsg = msgs[msgs.length - 1];
      if (lastMsg.role !== `agent`) return [];
      return extractRecommendations(lastMsg.content) || [];
    }
    return [];
  });

  const onSelectRecommendation = (prompt: string) => {
    starterSendMessage(prompt, true);
  };

  const onScrollStateChange = useCallback((isAtBottom: boolean) => {
    const next = !isAtBottom;
    setIsScrollButtonsShown((prev) => (prev === next ? prev : next));
  }, []);

  return (
    <Wrapper>
      {isLoadingSession.value && (
        <BackdropSpinner
          loading
          blurAmount={0}
          spinnerSize={40}
          backgroundColor="rgba(0,0,0,0)"
        />
      )}
      {showStarters.value && startersNode}
      {!showStarters.value && (
        <ChatMessages
          showTools={showTools}
          showReasoning={showReasoning}
          onScrollStateChange={onScrollStateChange}
          inputHeight={inputWrapperHeight}
          emptyMessage={emptyMessage}
        />
      )}
      <InputWrapper
        ref={inputWrapperRef}
        inputBottom={inputBottom}
        inputLeft={inputLeft}
        inputRight={inputRight}
      >
        <FloatingWrapper id={CHAT_INPUT_FLOATING_CONTAINER}>
          {followupsEnabled &&
            !isScrollButtonsShown &&
            recommendations.value.length > 0 && (
              <FollowupSuggestions
                recommendations={recommendations.value}
                onSelect={onSelectRecommendation}
              />
            )}
        </FloatingWrapper>
        <ChatInput
          ref={chatRef}
          disabled={!(selectedAgent || isLoadingSession.value)}
          showDropZone={dropzoneVisible}
          onSubmit={sendUserMessage}
          onAbort={abortMessage}
          loading={isProcessing.value}
          enableFilesUpload={enableFilesUpload}
          enableExamples={enableExamples}
          enableVoiceInput={enableVoiceInput}
          enableAgentSelection={enableAgentSelection}
          contextualAttachments={contextualAttachments}
          onEditAttachment={onEditAttachment}
          onAgentSelect={onAgentSelect}
          selectedAgent={selectedAgent}
          sessionId={currentSessionId.value}
          placeholder={placeholder}
        />
      </InputWrapper>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  position: relative;
  height: 100%;
  height: 100dvh;
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const FloatingWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
`;

const InputWrapper = styled.div`
  position: absolute;
  bottom: calc(
    ${(p) => p.inputBottom || `3rem`} + env(safe-area-inset-bottom, 0px)
  );
  left: ${(p) => p.inputLeft || `0`};
  right: ${(p) => p.inputRight || `0`};
`;
